import { useId, useState } from 'react';
import { cn } from '../../../lib/cn';
import { nearestIndex, type ChartMarker, type ChartPoint, type ChartThreshold } from '../../../lib/metrics';
import { formatDateTime } from '../../../lib/time';
import { Button, Card, EmptyState } from '../../atoms';
import { SeriesTable } from '../../molecules';
import { useChartSync } from './chartSync';
import { LineChart } from './LineChart';

export interface ChartCardProps {
  title: string;
  subtitle: string;
  points: ChartPoint[];
  fromMs: number;
  toMs: number;
  binMs: number;
  pollIntervalMs: number;
  formatValue: (value: number) => string;
  formatTick: (value: number) => string;
  domain?: [number, number];
  threshold?: ChartThreshold;
  markers?: ChartMarker[];
  wide?: boolean;
  /** Chart height including the x-axis band. */
  height?: number;
}

/**
 * A metric chart with a live readout in its header (latest value, or the value under the shared
 * crosshair) and a table view of the same data (the accessible equivalent).
 */
export function ChartCard({ title, subtitle, wide, height = 200, ...chart }: ChartCardProps) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const titleId = useId();
  const sync = useChartSync();
  const hoverIndex = sync.t !== null ? nearestIndex(chart.points, sync.t) : null;
  const readout = hoverIndex !== null ? chart.points[hoverIndex] : chart.points[chart.points.length - 1];

  return (
    <Card as="section" className={cn('px-5 pb-4 pt-4', wide && 'col-span-full')} aria-labelledby={titleId}>
      <header className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id={titleId} className="text-base">
            {title}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">{subtitle}</p>
        </div>
        <div className="flex flex-none items-start gap-2">
          {readout && (
            <div className="text-right" aria-live="off">
              <div className="font-mono text-lg font-medium leading-tight">{chart.formatValue(readout.avg)}</div>
              <div className="font-mono text-[11px] text-fg-muted">{hoverIndex !== null ? formatDateTime(readout.t) : 'latest'}</div>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            aria-pressed={view === 'table'}
            onClick={() => setView(view === 'chart' ? 'table' : 'chart')}
          >
            {view === 'chart' ? 'Table' : 'Chart'}
          </Button>
        </div>
      </header>

      {chart.points.length === 0 ? (
        <EmptyState height={height}>No polls recorded in this range</EmptyState>
      ) : view === 'chart' ? (
        <LineChart {...chart} height={height} ariaLabel={`${title}: ${subtitle}. Use arrow keys to read values.`} />
      ) : (
        <SeriesTable points={chart.points} formatValue={chart.formatValue} maxHeight={height} />
      )}
    </Card>
  );
}
